/**
 * Billing account slice pieces — the per-customer billing account record and
 * the general/status/type/frequency filters behind the accounts table.
 *
 * A billing account payload arrives with its notes and agreements nested, so
 * the load reducers here normalize and upsert into all three collections.
 *
 * Contains:
 * - billingAccountsInitialState / billingAccountsReducers — adapter state +
 *   the accounts table's view setters (paging, sort, status/type filters)
 * - addBillingAccountCases — load-one / load-list extra reducers
 * - billing account selectors
 */
import { normalize } from 'normalizr';

import { loadBillingAccount, loadBillingAccounts } from '@sophocles/features/billing/api';
import { BillingAccountSchema } from '@sophocles/features/billing/schema';
import {
  billingAccountAgreementsAdapter,
  billingAccountNotesAdapter,
  billingAccountsAdapter,
} from '@sophocles/features/billing/slices/adapters';

export const billingAccountsInitialState = billingAccountsAdapter.getInitialState({
  view: {
    search: null,
    page: 0,
    pageSize: 25,
    order: 'asc',
    orderBy: 'customer',
    billingStatus: 'all',
    customerType: 'all',
    frequency: 'all',
  },
});

export const billingAccountsReducers = {
  setBillingAccountPage(state, action) {
    state.accounts.view.page = action.payload;
  },
  setBillingAccountPageSize(state, action) {
    state.accounts.view.pageSize = action.payload;
  },
  setBillingAccountOrder(state, action) {
    state.accounts.view.order = action.payload;
  },
  setBillingAccountOrderBy(state, action) {
    state.accounts.view.orderBy = action.payload;
  },
  setBillingAccountFrequency(state, action) {
    state.accounts.view.frequency = action.payload;
  },
  setBillingAccountBillingStatus(state, action) {
    state.accounts.view.billingStatus = action.payload;
  },
  setBillingAccountCustomerType(state, action) {
    state.accounts.view.customerType = action.payload;
  },
};

export const addBillingAccountCases = builder => {
  builder
    .addCase(loadBillingAccount.fulfilled, (state, action) => {
      const normalized = normalize(action.payload, BillingAccountSchema);
      billingAccountsAdapter.upsertMany(state.accounts, normalized.entities.billingAccounts);
      if (normalized.entities.billingAccountNotes) {
        billingAccountNotesAdapter.upsertMany(state.accountNotes, normalized.entities.billingAccountNotes);
      }
      if (normalized.entities.billingAgreements) {
        billingAccountAgreementsAdapter.upsertMany(state.accountAgreements, normalized.entities.billingAgreements);
      }
    })
    .addCase(loadBillingAccounts.fulfilled, (state, action) => {
      if (action.payload.results.length === 0) {
        return;
      }
      const normalized = normalize(action.payload.results, [BillingAccountSchema]);
      billingAccountsAdapter.upsertMany(state.accounts, normalized.entities.billingAccounts);
      if (normalized.entities.billingAccountNotes) {
        billingAccountNotesAdapter.upsertMany(state.accountNotes, normalized.entities.billingAccountNotes);
      }
      if (normalized.entities.billingAgreements) {
        billingAccountAgreementsAdapter.upsertMany(state.accountAgreements, normalized.entities.billingAgreements);
      }
    });
};

export const selectBillingAccountEntities = state => {
  return state.billing.accounts;
};
export const { selectById: selectBillingAccountById } =
  billingAccountsAdapter.getSelectors(selectBillingAccountEntities);
export const selectBillingAccount = id => state => selectBillingAccountById(state, id);
